import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, fmtUSD } from "../lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, setQty, remove, subtotal, open, setOpen } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem("resellz_email") || "");

  const validEmail = /.+@.+\..+/.test(email);

  const handleCheckout = async () => {
    if (items.length === 0 || !validEmail) return;
    localStorage.setItem("resellz_email", email);
    setLoading(true);
    try {
      const r = await api.post("/checkout/session", {
        items: items.map((it) => ({ product_id: it.product.id, quantity: it.qty })),
        origin_url: window.location.origin,
        customer_email: email,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error("Could not start checkout. Try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="bg-[#0A0A0C] border-l border-white/10 text-white w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="font-serif text-2xl italic">Your bag</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {items.length === 0 && (
            <div className="text-white/50 text-sm" data-testid="cart-empty">
              Your bag is empty. Tap any piece to add it.
            </div>
          )}
          {items.map((it) => (
            <div key={it.product.id} className="flex gap-4" data-testid={`cart-item-${it.product.id}`}>
              <img src={it.product.image} alt={it.product.name} className="w-20 h-24 object-cover bg-white/5" />
              <div className="flex-1 min-w-0">
                <div className="font-serif text-base leading-tight truncate">{it.product.name}</div>
                <div className="text-xs text-white/40 mt-1">{fmtUSD(it.product.price)}</div>
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQty(it.product.id, it.qty - 1)}
                    data-testid={`decrease-${it.product.id}`}
                    className="w-7 h-7 inline-flex items-center justify-center border border-white/20 hover:bg-white/5"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-sm tabular-nums w-5 text-center">{it.qty}</span>
                  <button
                    type="button"
                    onClick={() => setQty(it.product.id, it.qty + 1)}
                    data-testid={`increase-${it.product.id}`}
                    className="w-7 h-7 inline-flex items-center justify-center border border-white/20 hover:bg-white/5"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(it.product.id)}
                    data-testid={`remove-${it.product.id}`}
                    className="ml-auto text-white/40 hover:text-white"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-6 border-t border-white/10 space-y-4">
          <div className="flex items-center justify-between">
            <span className="overline">Subtotal</span>
            <span data-testid="cart-subtotal" className="font-serif text-2xl">{fmtUSD(subtotal)}</span>
          </div>

          <div>
            <label className="overline block mb-2" htmlFor="cart-email">Email for receipt</label>
            <input
              id="cart-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="cart-email-input"
              className="w-full bg-white/5 border border-white/15 px-3 py-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-white"
            />
            <p className="text-[11px] text-white/40 mt-2">We&apos;ll email a receipt + vendor brochure PDF here.</p>
          </div>

          <button
            type="button"
            disabled={items.length === 0 || !validEmail || loading}
            onClick={handleCheckout}
            data-testid="checkout-stripe-button"
            className="w-full py-4 bg-white text-black uppercase tracking-[0.22em] text-xs font-semibold hover:bg-white/90 disabled:opacity-40 transition"
          >
            {loading ? "Redirecting…" : "Checkout · Stripe"}
          </button>
          <p className="text-[11px] text-white/40 leading-relaxed">
            Secure payment via Stripe.{" "}
            <a href="https://tiktok.com/@ayush.resellz" target="_blank" rel="noreferrer" className="underline">DM @ayush.resellz</a>{" "}for help.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
